import React, { Fragment } from 'react';
import { Breakpoint } from 'react-socks';
import { common } from '@kurtosys/ksys-app-template';
import { IFormatter } from '@kurtosys/types/data/IFormatter.js';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { InjectedStyledComponent, ResponsiveComponentBase } from '../../../shared/index.js';
import Color from '../../base/Color/Color.js';
import List from '../../base/List/List.js';
import { ITranslationContextValue } from '../../base/Translate/models/index.js';
import Translate, { TranslationContext } from '../../base/Translate/Translate.js';

import { IChartLegendFormatters } from './models/IChartLegendFormatters.js';
import { IChartLegendConfiguration, IChartLegendProps, IChartLegendState } from './models/index.js';
import TotalText from './styledComponents/TotalText.js';
import TotalValue from './styledComponents/TotalValue.js';
import Wrapper from './styledComponents/Wrapper.js';
import { CHART_LEGEND_CATEGORY_KEY, CHART_LEGEND_COMPONENT_KEY } from './ChartLegend.meta.js';

export class ChartLegend extends ResponsiveComponentBase<
	IChartLegendProps,
	IChartLegendState,
	IChartLegendConfiguration
> {
	categoryKey = CHART_LEGEND_CATEGORY_KEY;
	componentKey = CHART_LEGEND_COMPONENT_KEY;
	formatters = {};

	constructor(props: IChartLegendProps) {
		super(props);

		makeObservable(this, {
			listItems: computed,
		});
	}

	get listItems() {
		const { data, colors, showTotal, totalText = 'Total', labelKey, valueKey } = this.props;
		const valueFormatter = this.getFormatter(this.formatters, 'value');
		const labelFormatter = this.getFormatter(this.formatters, 'label');
		let response: any[] = [];
		if (data) {
			let total = 0;
			response = data.map((item, idx) => {
				const itemKeys = Object.keys(item).filter((key) => key !== 'color');
				const color = item.color || (colors ? colors[idx % colors.length] : '#000');
				const label = item[labelKey || itemKeys[0]];
				const value = item[valueKey || itemKeys[1]];
				if (common.commonUtils.isNumber(value)) {
					total += value;
				}
				return [<Color key={`${idx}_${color}`} color={color} />, labelFormatter(label), valueFormatter(value)];
			});
			if (showTotal) {
				response.push([
					null,
					<TotalText key={'totalText'}>
						<Translate>{totalText}</Translate>
					</TotalText>,
					<TotalValue key={'totalValue'}>{valueFormatter(total)}</TotalValue>,
				]);
			}
		}
		return response;
	}

	getFormatter(formatters: IChartLegendFormatters, type: 'value' | 'label'): (value: any) => any {
		if (!common.commonUtils.isObjectAndEmpty(formatters)) {
			if (type === 'value') {
				const valueFormatter = formatters.valueFormatter;
				if (valueFormatter && typeof valueFormatter === 'function') {
					return valueFormatter;
				}
			}
			if (type === 'label') {
				const labelFormatter = formatters.labelFormatter;
				if (labelFormatter && typeof labelFormatter === 'function') {
					return labelFormatter;
				}
			}
		}
		return (value: any) => value;
	}

	applyFormatters(translationContext: ITranslationContextValue) {
		const { formatters } = this.props;
		if (formatters) {
			const valueFormatterConfiguration = formatters.valueFormatter;
			if (valueFormatterConfiguration && typeof valueFormatterConfiguration === 'object') {
				this.formatters = Object.assign({}, this.formatters, {
					valueFormatter: (value: any) => {
						if (!valueFormatterConfiguration.locale) {
							valueFormatterConfiguration.locale = translationContext && translationContext.culture;
						}
						if (!valueFormatterConfiguration.options) {
							valueFormatterConfiguration.options = {};
						}
						return common.commonUtils.formatter(valueFormatterConfiguration as IFormatter, value);
					},
				});
			}
			const labelFormatterConfiguration = formatters.labelFormatter;
			if (labelFormatterConfiguration && typeof labelFormatterConfiguration === 'object') {
				this.formatters = Object.assign({}, this.formatters, {
					labelFormatter: (value: any) => {
						if (!labelFormatterConfiguration.locale) {
							labelFormatterConfiguration.locale = translationContext && translationContext.culture;
						}
						if (!labelFormatterConfiguration.options) {
							labelFormatterConfiguration.options = {};
						}
						return common.commonUtils.formatter(labelFormatterConfiguration as IFormatter, value);
					},
				});
			}
		}
	}

	render() {
		const { className } = this.props;
		const breakpoints = this.breakpoints;
		return (
			<Fragment>
				{breakpoints.map((breakpoint, breakpointIndex) => {
					const { props } = breakpoint;
					const breakpointProps = this.getBreakpointComponentProps(breakpoint, breakpointIndex);
					if (!props) {
						return null;
					}
					return (
						<Breakpoint key={breakpointIndex} {...breakpointProps}>
							<Wrapper className={className}>
								<TranslationContext.Consumer>
									{(translationContext) => {
										this.applyFormatters(translationContext);
										return (
											<List
												heading={props.heading}
												items={this.listItems}
												itemStyle={'justified'}
											/>
										);
									}}
								</TranslationContext.Consumer>
							</Wrapper>
						</Breakpoint>
					);
				})}
			</Fragment>
		);
	}
}

export default InjectedStyledComponent(observer(ChartLegend), CHART_LEGEND_CATEGORY_KEY, CHART_LEGEND_COMPONENT_KEY);
